import { useState } from "react";
import { Link } from "react-router";
import type { Place } from "@shared/schema.ts";
import { applyMapsHintToPlace } from "@shared/maps-place.ts";
import { placeDirectionsUrl, safeHttpUrl } from "../lib/format.ts";
import { humanStatus } from "@shared/labels.ts";
import { useSession } from "../app/session.tsx";
import { useTrip } from "../app/trip.tsx";
import { readMapsPlace } from "../lib/api.ts";

export function PlaceActions({ place, compact }: { place: Place; compact?: boolean }) {
  const href = placeDirectionsUrl(place);
  const exact = Boolean(safeHttpUrl(place.mapsUrl) || place.coordinates?.verified);
  const { role } = useSession();
  const { trip, save, saveState } = useTrip();
  const [fillMessage, setFillMessage] = useState<string | null>(null);
  const missingAddress = Boolean(place.mapsUrl && !place.address);
  const canFill = role === "owner" && missingAddress && trip;

  async function fillFromMapLink() {
    if (!place.mapsUrl || !trip) return;
    setFillMessage(null);
    try {
      const { hint } = await readMapsPlace(place.mapsUrl);
      const asOf = new Date().toISOString().slice(0, 10);
      const nextPlace = applyMapsHintToPlace(place, hint, asOf);
      if (!nextPlace.address) {
        setFillMessage(hint.note ?? "No street address in that map link. Add it when you edit the place.");
        return;
      }
      const result = await save({
        ...trip,
        places: trip.places.map((row) => (row.id === place.id ? nextPlace : row)),
      });
      setFillMessage(result.ok ? "Address saved for the driver card." : result.message);
    } catch (error) {
      setFillMessage(error instanceof Error ? error.message : "Could not read that map link.");
    }
  }

  return (
    <div className="row-actions">
      {place.address || place.mapsUrl || place.coordinates?.verified ? (
        <a className="btn" href={href} data-place-nav={place.id} target="_blank" rel="noreferrer">
          {exact ? "Open map link" : "Search in maps"}
        </a>
      ) : (
        <p className="muted">No map link yet.</p>
      )}
      <Link className={compact ? "btn-ghost" : "btn-secondary"} to={`/show/driver/${place.id}`}>
        Show the driver
      </Link>
      {canFill ? (
        <button
          className="btn-secondary"
          type="button"
          disabled={saveState === "saving"}
          onClick={() => void fillFromMapLink()}
        >
          {saveState === "saving" ? "Saving address…" : "Fill address from map link"}
        </button>
      ) : null}
      {fillMessage ? <p className="muted">{fillMessage}</p> : null}
      {place.precision ? <p className="muted">{humanStatus(place.precision)}</p> : null}
    </div>
  );
}
